import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroSection from "@/components/sections/HeroSection";
import AboutSection from "@/components/sections/AboutSection";
import ServicesSection from "@/components/sections/ServicesSection";
import StatsSection from "@/components/sections/StatsSection";
import FAQSection from "@/components/sections/FAQSection";
import ContactSection from "@/components/sections/ContactSection";
import ReviewSection from "@/components/sections/ReviewSection";
import PricingCalculator from "@/components/sections/PricingCalculator";
import AppDownloadSection from "@/components/sections/AppDownloadSection";
import TeamSection from "@/components/sections/TeamSection";
import GetStartedWizard from "@/components/GetStartedWizard";
import { scrollToSection } from "@/lib/scrollToSection";

export default function HomePage() {
  const [showWizard, setShowWizard] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setShowWizard(true);
    window.addEventListener("koko-open-wizard", handler);
    return () => window.removeEventListener("koko-open-wizard", handler);
  }, []);

  // Cross-page anchor scroll: when Navbar redirects from /blog → /#pricing
  // we receive the target in location.state; run after mount so sections exist.
  useEffect(() => {
    const target = location.state?.scrollTo;
    if (!target) return;
    const t1 = setTimeout(() => scrollToSection(target), 120);
    const t2 = setTimeout(() => scrollToSection(target), 450);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [location.state]);

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#0A0A0A]">
      <Helmet>
        <title>KoKo Sites - Custom Web Design Studio in India | Affordable Websites from Rs 15,000 | Bolpur Santiniketan</title>
        <meta name="description" content="KoKo Sites - Independent web design studio based in Bolpur Santiniketan, West Bengal. Custom websites for Indian businesses starting at Rs 15,000. Gym, dental clinic, restaurant, salon, law firm, real estate, pharmacy, bakery & more. Contact Kamran Aziz: +91 92424 78118" />
        <meta name="keywords" content="web design India, website developer Bolpur, web designer Santiniketan, KoKo Sites, affordable website India, Kamran Aziz web designer, website design Kolkata, web development West Bengal, freelance web designer India, website maker near me, website design Mumbai, website design Delhi, website design Bangalore, website design Chennai, website design Hyderabad, website design Pune, gym website, dental clinic website, restaurant website, salon website, law firm website, real estate website, ecommerce website India, website Rs 15000, kokosites" />
        <link rel="canonical" href="https://kokosites.in" />
        <meta property="og:title" content="KoKo Sites - Custom Web Design Studio in India | Websites from Rs 15,000" />
        <meta property="og:description" content="Custom websites for Indian businesses. Gym, dental, restaurant, salon, law firm & more. Based in Bolpur Santiniketan. WhatsApp: +91 92424 78118" />
        <meta property="og:url" content="https://kokosites.in" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:title" content="KoKo Sites - Web Design Studio India | Websites from Rs 15,000" />
        <meta name="twitter:description" content="Custom websites for Indian businesses. Gym, dental, restaurant, salon & more. Based in Bolpur Santiniketan. Contact: +91 92424 78118" />
      </Helmet>
      <Navbar />
      <main>
        <HeroSection onGetStarted={() => setShowWizard(true)} />
        <AboutSection />
        <ServicesSection />
        <TeamSection />
        <StatsSection />
        <PricingCalculator />
        <ReviewSection />
        <AppDownloadSection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
      {showWizard && <GetStartedWizard onClose={() => setShowWizard(false)} />}
    </div>
  );
}
